import { authErrorMessage, type ProgrammeCode } from "@studyward/shared";
import { z } from "zod";
import { trackClient } from "./analytics";
import { authStorage, supabase } from "./api";
import { authCallbackUrl, env } from "./env";

export type OAuthProvider = "google" | "apple";
export const PROVIDER_NAMES: Record<OAuthProvider, string> = { google: "Google", apple: "Apple" };

export async function signInWithEmail(email: string, password: string, remember = true) {
  authStorage.setRememberMe(remember);
  const { error } = await supabase.auth.signInWithPassword({ email: email.trim(), password });
  if (error) throw new Error(authErrorMessage(error));
  trackClient("signin_completed", { method: "email" });
}

/** The programme picked at sign-up rides along in user metadata so setup can pre-fill it (§10.2 #2). */
export async function signUpWithEmail(input: { name: string; email: string; password: string; programme: ProgrammeCode | null }) {
  const { data, error } = await supabase.auth.signUp({
    email: input.email.trim(),
    password: input.password,
    options: { data: { full_name: input.name.trim(), programme: input.programme }, emailRedirectTo: authCallbackUrl() },
  });
  if (error) throw new Error(authErrorMessage(error));
  // With email confirmation on, an address that already has an account comes back with no identities.
  if (data.user && data.user.identities?.length === 0) throw new Error(authErrorMessage({ code: "user_already_exists" }));
  return { needsConfirmation: !data.session };
}

export async function resendConfirmation(email: string) {
  const { error } = await supabase.auth.resend({ type: "signup", email: email.trim(), options: { emailRedirectTo: authCallbackUrl() } });
  if (error) throw new Error(authErrorMessage(error));
}

export async function sendPasswordReset(email: string) {
  const { error } = await supabase.auth.resetPasswordForEmail(email.trim(), { redirectTo: `${window.location.origin}/auth/new-password` });
  if (error) throw new Error(authErrorMessage(error));
}

export async function setNewPassword(password: string) {
  const { error } = await supabase.auth.updateUser({ password });
  if (error) throw new Error(authErrorMessage(error));
}

/** Sends the page to Google/Apple; the round trip ends on /auth/callback. */
export async function signInWithProvider(provider: OAuthProvider, remember = true) {
  authStorage.setRememberMe(remember);
  sessionStorage.setItem("sw.oauth", provider);
  const { error } = await supabase.auth.signInWithOAuth({ provider, options: { redirectTo: authCallbackUrl() } });
  if (error) throw new Error(`${PROVIDER_NAMES[provider]} sign-in isn't available right now. Use email instead.`);
}

/** Records the sign-in method once an OAuth round trip lands (the page reloaded in between). */
export function trackOAuthLanding() {
  const provider = sessionStorage.getItem("sw.oauth");
  sessionStorage.removeItem("sw.oauth");
  if (provider === "google" || provider === "apple") trackClient("signin_completed", { method: provider });
}

export async function signOut() {
  await supabase.auth.signOut();
}

const authSettingsSchema = z.object({ external: z.record(z.string(), z.boolean()).default({}) });

/** Social sign-ins switched on in Supabase Auth, so the page never shows a button that can't work. */
export async function enabledProviders(): Promise<OAuthProvider[]> {
  const res = await fetch(`${env.supabaseUrl}/auth/v1/settings`, { headers: { apikey: env.supabaseAnonKey } });
  if (!res.ok) return [];
  const settings = authSettingsSchema.parse(await res.json());
  return (["google", "apple"] as const).filter((p) => settings.external[p]);
}

const geoSchema = z.object({ country: z.string().nullable(), allowed: z.boolean() });

/**
 * US-only signup gate (§8.2) from the web host's /_edge/geo. A launch-scope check, not a security control: when the
 * edge route can't answer (local dev serves the SPA there), signup stays open.
 */
export async function signupRegion(): Promise<z.infer<typeof geoSchema>> {
  try {
    const res = await fetch("/_edge/geo", { headers: { Accept: "application/json" } });
    const parsed = geoSchema.safeParse(await res.json());
    return parsed.success ? parsed.data : { country: null, allowed: true };
  } catch {
    return { country: null, allowed: true };
  }
}
